import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowLeft, Edit, Eye, Plus, Trash2, Sparkles, Upload } from "lucide-react";
import { getAuditType } from "@/lib/auditTypes";

const BUILT_INS = ["gdpr", "nis2", "iso27001", "dora", "ai_act"];

export default function TemplateCategory() {
  const { category, sector } = useParams();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [tagCatalog, setTagCatalog] = useState([]);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiName, setAiName] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importType, setImportType] = useState("csv");
  const [importName, setImportName] = useState("");
  const [manualOpen, setManualOpen] = useState(false);
  const [actionTagName, setActionTagName] = useState("");
  const isGeneral = sector === "generale";
  const actionTag = !sector ? actionTagName.trim() : (isGeneral ? "" : sector);
  const label = BUILT_INS.includes(category) ? getAuditType(category).label : category === "custom" ? "Altre checklist" : category;

  const load = () => Promise.all([api.get("/templates"), api.get("/company-tags")]).then(([templatesResponse, tagsResponse]) => { setTemplates(templatesResponse.data.filter((t) => (t.category || (t.built_in ? t.type : "custom")) === category)); setTagCatalog(tagsResponse.data); }).catch(() => toast.error("Errore caricamento"));
  // load intentionally changes only when the selected category changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [category, sector]);

  const remove = async (template) => {
    try {
      await api.delete(`/templates/${template.template_id}`);
      toast.success(template.overridden ? "Personalizzazione rimossa" : "Checklist eliminata");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore eliminazione"); }
  };

  const removeTag = async (tag) => {
    try {
      const catalogTag = tagCatalog.find((item) => item.name === tag.key);
      if (!catalogTag) return;
      await api.delete(`/company-tags/${catalogTag.id}`);
      toast.success(`TAG "${tag.label}" eliminato`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Impossibile eliminare il TAG"); }
  };

  const generateWithAi = async () => {
    if (aiText.trim().length < 50) { toast.error("Inserisci almeno 50 caratteri"); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post("/templates/generate-from-text", { text: aiText, name: aiName || null, category });
      for (let attempt = 0; attempt < 100; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        const { data: job } = await api.get(`/generation-jobs/${data.job_id}`);
        if (job.status === "done") { toast.success("Checklist generata"); setAiOpen(false); setAiText(""); setAiName(""); load(); return; }
        if (job.status === "error") throw new Error(job.error || "Errore generazione AI");
      }
      throw new Error("Generazione troppo lunga");
    } catch (err) { toast.error(err?.response?.data?.detail || err.message || "Errore generazione AI"); }
    finally { setAiLoading(false); }
  };

  const importTemplate = async () => {
    if (!importFile) { toast.error("Seleziona un file"); return; }
    let createdTag = null;
    try {
      if (!sector) {
        if (!actionTagName.trim()) { toast.error("Inserisci il nome della sotto-categoria"); return; }
        const { data } = await api.post("/company-tags", { name: actionTagName.trim() });
        createdTag = data;
      }
      const body = new FormData(); body.append("file", importFile);
      const params = `name=${encodeURIComponent(importName || importFile.name)}&category=${encodeURIComponent(category)}&industry_tags=${encodeURIComponent(actionTag)}`;
      await api.post(`/templates/import/${importType}?${params}`, body, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Template importato"); setImportOpen(false); setImportFile(null); setImportName(""); load();
    } catch (err) {
      if (createdTag) await api.delete(`/company-tags/${createdTag.id}`).catch(() => {});
      toast.error(err?.response?.data?.detail || "Errore importazione");
    }
  };

  const openManual = () => { setActionTagName(""); setImportName(""); setManualOpen(true); };
  const createManual = async () => {
    if (!importName.trim()) { toast.error("Inserisci il nome della checklist"); return; }
    try {
      let tag = actionTag;
      if (!sector) {
        if (!actionTagName.trim()) { toast.error("Inserisci il nome della sotto-categoria"); return; }
        const { data } = await api.post("/company-tags", { name: actionTagName.trim() });
        tag = data.name;
      }
      setManualOpen(false);
      navigate(`/app/template/nuovo?category=${encodeURIComponent(category)}&name=${encodeURIComponent(importName.trim())}&industry_tag=${encodeURIComponent(tag)}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore creazione sotto-categoria"); }
  };

  const sectorTemplates = templates.filter((template) => {
    const tags = template.industry_tags || [];
    if (!sector) return true;
    return sector === "generale" ? tags.length === 0 : tags.includes(sector);
  });
  const sectors = [{ key: "generale", label: "Generale", count: templates.filter((template) => !(template.industry_tags || []).length).length }]
    .concat(tagCatalog.map((tag) => ({ key: tag.name, label: tag.name, count: templates.filter((template) => (template.industry_tags || []).includes(tag.name)).length })));

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <div className="flex items-center gap-3 mb-8">
        <Link to={sector ? `/app/template/categoria/${encodeURIComponent(category)}` : "/app/template"} className="text-slate-500 hover:text-slate-900"><ArrowLeft className="h-5 w-5" /></Link>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">Template</div>
          <h1 className="font-display font-black text-4xl">{label}{sector ? ` · ${sector === "generale" ? "Generale" : sector}` : ""}</h1>
        </div>
      </div>

      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <p className="text-sm text-slate-500">{sector ? `Checklist ${label} · ${sector === "generale" ? "Generale" : sector}.` : `Scegli il settore aziendale per ${label}.`}</p>
         <div className="flex items-center gap-2 flex-wrap">
           <Button variant="outline" onClick={() => setAiOpen(true)} data-testid="category-ai-generate-btn"><Sparkles className="h-4 w-4" /> Genera con AI</Button>
           <Button variant="outline" onClick={openManual} data-testid="new-category-template-btn"><Plus className="h-4 w-4" /> Crea manualmente</Button>
           <Button onClick={() => setImportOpen(true)} data-testid="category-import-btn" className="bg-slate-900 hover:bg-slate-800"><Upload className="h-4 w-4" /> Importa</Button>
         </div>
       </div>

       <Dialog open={aiOpen} onOpenChange={setAiOpen}>
         <DialogContent className="max-w-2xl"><DialogHeader><DialogTitle>Genera checklist con AI · {label}</DialogTitle></DialogHeader>
           <div className="space-y-4"><Input value={aiName} onChange={(e) => setAiName(e.target.value)} placeholder="Nome checklist (opzionale)" /><Textarea value={aiText} onChange={(e) => setAiText(e.target.value)} rows={12} placeholder="Incolla il testo normativo..." /><div className="text-xs text-slate-500">{aiText.length} caratteri</div></div>
           <DialogFooter><Button onClick={generateWithAi} disabled={aiLoading} className="bg-indigo-600 hover:bg-indigo-700"><Sparkles className="h-4 w-4" /> {aiLoading ? "Generazione in corso…" : "Genera checklist"}</Button></DialogFooter>
         </DialogContent>
       </Dialog>

       <Dialog open={manualOpen} onOpenChange={setManualOpen}>
         <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Crea checklist · {label}</DialogTitle></DialogHeader>
           <div className="space-y-4">
             {!sector && <Input value={actionTagName} onChange={(e) => setActionTagName(e.target.value)} placeholder="Nome sotto-categoria / TAG" required />}
             {sector && <div className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">{isGeneral ? "La checklist verrà salvata in Generale." : `TAG: ${sector}`}</div>}
             <Input value={importName} onChange={(e) => setImportName(e.target.value)} placeholder="Nome checklist" required />
           </div>
           <DialogFooter><Button onClick={createManual} className="bg-slate-900 hover:bg-slate-800"><Plus className="h-4 w-4" /> Continua</Button></DialogFooter>
         </DialogContent>
       </Dialog>

       <Dialog open={importOpen} onOpenChange={setImportOpen}>
         <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Importa checklist · {label}</DialogTitle></DialogHeader>
           <div className="space-y-4">{!sector && <Input value={actionTagName} onChange={(e) => setActionTagName(e.target.value)} placeholder="Nome sotto-categoria / TAG" required />}{sector && <div className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">{isGeneral ? "Il file verrà importato in Generale." : `TAG: ${sector}`}</div>}<select value={importType} onChange={(e) => setImportType(e.target.value)} className="h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"><option value="csv">CSV</option><option value="xlsx">Excel</option></select><Input value={importName} onChange={(e) => setImportName(e.target.value)} placeholder="Nome checklist (opzionale)" /><Input type="file" accept={importType === "csv" ? ".csv,text/csv" : ".xlsx"} onChange={(e) => setImportFile(e.target.files?.[0] || null)} /></div>
           <DialogFooter><Button onClick={importTemplate} className="bg-slate-900 hover:bg-slate-800"><Upload className="h-4 w-4" /> Importa</Button></DialogFooter>
         </DialogContent>
       </Dialog>

      {!sector ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
           {sectors.map((item) => item.key === "generale" ? (
             <Link key={item.key} to={`/app/template/categoria/${encodeURIComponent(category)}/settore/${encodeURIComponent(item.key)}`} data-testid={`template-sector-${item.key}`} className="group bg-white border border-slate-200 rounded-lg p-5 hover:border-slate-400 hover:shadow-sm transition-all">
               <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">Settore aziendale</div>
               <div className="flex items-center justify-between gap-3"><h2 className="font-display font-bold text-xl text-slate-900 group-hover:text-blue-700">{item.label}</h2><span className="font-mono-plex text-2xl font-bold text-slate-900">{item.count}</span></div>
               <div className="mt-1 text-xs text-slate-500">{item.count === 1 ? "checklist disponibile" : "checklist disponibili"}</div>
             </Link>
           ) : (
             <div key={item.key} className="group bg-white border border-slate-200 rounded-lg p-5 hover:border-slate-400 hover:shadow-sm transition-all">
               <Link to={`/app/template/categoria/${encodeURIComponent(category)}/settore/${encodeURIComponent(item.key)}`} data-testid={`template-sector-${item.key}`} className="block">
                 <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">TAG / Settore</div>
                 <div className="flex items-center justify-between gap-3"><h2 className="font-display font-bold text-xl text-slate-900 group-hover:text-blue-700">{item.label}</h2><span className="font-mono-plex text-2xl font-bold text-slate-900">{item.count}</span></div>
                 <div className="mt-1 text-xs text-slate-500">{item.count === 1 ? "checklist disponibile" : "checklist disponibili"}</div>
               </Link>
               {item.count === 0 && <AlertDialog>
                 <AlertDialogTrigger asChild><button type="button" data-testid={`delete-template-sector-${item.key}`} className="mt-4 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /> Elimina TAG</button></AlertDialogTrigger>
                 <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Eliminare il TAG "{item.label}"?</AlertDialogTitle><AlertDialogDescription>Il TAG verrà rimosso dal tenant. L’operazione è possibile solo se non è ancora utilizzato.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction onClick={() => removeTag(item)} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
               </AlertDialog>}
             </div>
           ))}
        </div>
      ) : sectorTemplates.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-14 text-center">
          <div className="font-display font-bold text-lg mb-2">Nessuna checklist in questa categoria</div>
          <p className="text-sm text-slate-500 mb-5">Crea la prima checklist personalizzata.</p>
           <Button onClick={openManual} className="bg-slate-900 hover:bg-slate-800"><Plus className="h-4 w-4" /> Crea checklist</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sectorTemplates.map((template) => (
            <div key={template.type} data-testid={`template-card-${template.type}`} className="bg-white border border-slate-200 rounded-lg p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${template.built_in ? "bg-slate-100 text-slate-700" : "bg-blue-50 text-blue-700"}`}>{template.built_in ? "Framework" : "Custom"}</span>
                    {template.overridden && <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">Personalizzato</span>}
                  </div>
                   <h2 className="font-display font-bold text-lg text-slate-900">{template.name}</h2>
                   {template.is_default && <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-amber-800">✓ Standard</div>}
                  {template.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{template.description}</p>}
                  <div className="text-xs text-slate-500 mt-3 font-mono-plex">{template.sections_count} sezioni · {template.items_count} controlli</div>
                </div>
                <div className="flex items-center gap-1">
                  <Link to={`/app/template/${template.type}`} className="p-2 text-slate-400 hover:text-slate-900" title="Visualizza"><Eye className="h-4 w-4" /></Link>
                  {(!template.built_in || template.overridden) && <Link to={`/app/template/${template.type}`} className="p-2 text-slate-400 hover:text-blue-600" title="Modifica"><Edit className="h-4 w-4" /></Link>}
                  {template.template_id && <AlertDialog>
                    <AlertDialogTrigger asChild><button className="p-2 text-slate-400 hover:text-red-600" title={template.overridden ? "Ripristina" : "Elimina"}><Trash2 className="h-4 w-4" /></button></AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>{template.overridden ? "Ripristinare il framework?" : "Eliminare la checklist?"}</AlertDialogTitle><AlertDialogDescription>{template.overridden ? "La checklist predefinita verrà ripristinata." : "La checklist non sarà più disponibile per nuovi audit."}</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction onClick={() => remove(template)} className="bg-red-600 hover:bg-red-700">{template.overridden ? "Ripristina" : "Elimina"}</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
